/**
 * Framer Motion animation primitives for the trading dashboard.
 *
 * All primitives respect `prefers-reduced-motion` — when the OS-level
 * setting is enabled, components render instantly without animation.
 *
 * Usage:
 *   <FadeIn>content</FadeIn>
 *   <SlideUp delay={0.1}>card</SlideUp>
 *   <StaggerChildren>
 *     <StaggerItem>item 1</StaggerItem>
 *     <StaggerItem>item 2</StaggerItem>
 *   </StaggerChildren>
 */

import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import * as React from "react";

const INSTANT = { duration: 0 };

export function FadeIn({
  children,
  className,
  ...props
}: HTMLMotionProps<"div"> & { children: React.ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={reduced ? INSTANT : { duration: 0.2, ease: "easeOut" }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function SlideUp({
  children,
  delay = 0,
  className,
  ...props
}: HTMLMotionProps<"div"> & { children: React.ReactNode; delay?: number }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduced ? INSTANT : { duration: 0.25, delay, ease: "easeOut" }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StaggerChildren({
  children,
  className,
  staggerDelay = 0.05,
  ...props
}: HTMLMotionProps<"div"> & {
  children: React.ReactNode;
  staggerDelay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: reduced
            ? { staggerChildren: 0 }
            : { staggerChildren: staggerDelay },
        },
      }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  ...props
}: HTMLMotionProps<"div"> & { children: React.ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      variants={
        reduced
          ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
          : { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }
      }
      transition={reduced ? INSTANT : { duration: 0.2, ease: "easeOut" }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function AnimatedNumber({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.span
      key={value}
      initial={reduced ? false : { opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduced ? INSTANT : { duration: 0.3 }}
      className={className}
    >
      {value}
    </motion.span>
  );
}
